export const ADD_ON_CART_STORAGE_KEY = 'sg.cart.addons.v1'
export const ADD_ON_CART_CHANGE_EVENT = 'sg:addon-cart-change'

export type CartAddOn = {
  id: string
  name: string
  packLabel: string
  shortDescription: string
  unitPriceCents: number
  imageUrl: string | null
  quantity: number
  maxQuantityPerOrder: number
  updatedAt: string
}

export type AddOnCartChangeDetail = {
  addOns: CartAddOn[]
}

function valid(value: unknown): CartAddOn | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const item = value as Record<string, unknown>
  if (typeof item.id !== 'string' || !item.id.trim()
    || typeof item.name !== 'string' || !item.name.trim()
    || typeof item.packLabel !== 'string' || !item.packLabel.trim()
    || typeof item.shortDescription !== 'string'
    || !Number.isInteger(item.unitPriceCents) || Number(item.unitPriceCents) < 1
    || (item.imageUrl !== null && typeof item.imageUrl !== 'string')
    || !Number.isInteger(item.quantity) || Number(item.quantity) < 1
    || !Number.isInteger(item.maxQuantityPerOrder) || Number(item.maxQuantityPerOrder) < 1
    || Number(item.quantity) > Number(item.maxQuantityPerOrder)
    || typeof item.updatedAt !== 'string') return null
  return {
    id: item.id.trim(),
    name: item.name.trim(),
    packLabel: item.packLabel.trim(),
    shortDescription: item.shortDescription.trim(),
    unitPriceCents: Number(item.unitPriceCents),
    imageUrl: typeof item.imageUrl === 'string' && item.imageUrl ? item.imageUrl : null,
    quantity: Number(item.quantity),
    maxQuantityPerOrder: Number(item.maxQuantityPerOrder),
    updatedAt: item.updatedAt,
  }
}

function publish(addOns: CartAddOn[]) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<AddOnCartChangeDetail>(ADD_ON_CART_CHANGE_EVENT, {
    detail: { addOns },
  }))
}

function persist(addOns: CartAddOn[]) {
  if (typeof window === 'undefined') return addOns
  try {
    if (addOns.length) window.localStorage.setItem(ADD_ON_CART_STORAGE_KEY, JSON.stringify(addOns))
    else window.localStorage.removeItem(ADD_ON_CART_STORAGE_KEY)
  } catch {
    // In-memory UI state remains usable even when storage is unavailable.
  }
  publish(addOns)
  return addOns
}

export function readCartAddOns(): CartAddOn[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(ADD_ON_CART_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const seen = new Set<string>()
    const addOns: CartAddOn[] = []
    for (const candidate of parsed) {
      const item = valid(candidate)
      if (!item || seen.has(item.id)) continue
      seen.add(item.id)
      addOns.push(item)
    }
    return addOns
  } catch {
    return []
  }
}

export function setCartAddOn(
  product: Omit<CartAddOn, 'quantity' | 'updatedAt'>,
  quantity: number,
): CartAddOn[] {
  if (!Number.isInteger(quantity) || quantity < 0 || quantity > product.maxQuantityPerOrder) {
    throw new RangeError('Add-on quantity is outside the allowed range.')
  }
  const current = readCartAddOns().filter((item) => item.id !== product.id)
  if (quantity === 0) return persist(current)
  return persist([...current, {
    ...product,
    quantity,
    updatedAt: new Date().toISOString(),
  }])
}

export function removeCartAddOn(id: string) {
  return persist(readCartAddOns().filter((item) => item.id !== id))
}

export function clearCartAddOns() {
  return persist([])
}

export function cartAddOnSubtotalCents(addOns = readCartAddOns()) {
  return addOns.reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0)
}
